<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { Separator as SphSeparator } from '@/components/base/separator';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

interface Club {
    name: string
    street: string
    zipcode: string
    city: string
    corporateType: number | undefined
    province: number | undefined
}

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    clubId?: number
    provinces: Record<string, string>
    corporateTypes: Record<string, string>
    backUrl: string
}>();

function createEmptyClub(): Club {
    return {
        name: '',
        street: '',
        zipcode: '',
        city: '',
        corporateType: undefined,
        province: undefined,
    };
}

const { i18n } = useI18n();
const { notify } = useNotifications();
const club = reactive<Club>(createEmptyClub());
const loading = ref(false);
const pending = ref(false);
const errors = ref<Record<string, string[]>>({});
const provinceOptions = computed(() =>
    Object.entries(props.provinces).map(([value, label]) => ({ value: Number(value), label })),
);
const corporateTypeOptions = computed(() =>
    Object.entries(props.corporateTypes).map(([value, label]) => ({ value: Number(value), label })),
);
const isEditMode = computed(() => props.clubId !== undefined);
const isValid = computed(() => {
    return club.name.trim().length > 0
        && club.street.trim().length > 0
        && club.zipcode.trim().length > 0
        && club.city.trim().length > 0;
});

onMounted(() => {
    if (isEditMode.value) {
        load();
    }
});

async function load() {
    loading.value = true;

    try {
        const response = await fetch(`/api/federation/clubs/${props.clubId}`);
        if (!response.ok) {
            throw new Error('Request failed');
        }

        const { data } = await response.json() as { data: Club };
        Object.assign(club, {
            ...data,
            corporateType: data.corporateType ?? undefined,
            province: data.province ?? undefined,
        });
    } catch {
        notify.error('error.loadingData');
    } finally {
        loading.value = false;
    }
}

async function save() {
    if (!isValid.value || pending.value) {
        return;
    }

    pending.value = true;
    errors.value = {};

    try {
        const url = isEditMode.value
            ? `/api/federation/clubs/${props.clubId}`
            : `/api/federation/federations/${props.federationId}/clubs`;

        const payload = {
            ...club,
            corporateType: club.corporateType || null,
            province: club.province || null,
        };

        const response = await fetch(url, {
            method: isEditMode.value ? 'PUT' : 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const details = errorData?.error?.details;
            if (Array.isArray(details)) {
                const fieldErrors: Record<string, string[]> = {};
                for (const detail of details) {
                    if (!Array.isArray(detail)) {
                        continue;
                    }
                    const attributeName = detail.find((e: { key: string, value: string }) => e.key === 'attributeName')?.value;
                    const message = detail.find((e: { key: string, value: string }) => e.key === 'constraintMessage')?.value;
                    if (attributeName && message) {
                        fieldErrors[attributeName] = [message];
                    }
                }
                errors.value = fieldErrors;
            }
            throw new Error(errorData.message || 'Request failed');
        }

        notify.success(
            isEditMode.value
                ? 'Die Änderungen wurden erfolgreich gespeichert.'
                : 'Der Verein wurde erfolgreich angelegt.',
            {},
            { saveInSession: true },
        );

        window.location.href = props.backUrl;
    } catch {
        notify.error(isEditMode.value
            ? 'Die Änderungen konnten nicht gespeichert werden.'
            : 'Der Verein konnte nicht angelegt werden.');
    } finally {
        pending.value = false;
    }
}

function cancel() {
    window.location.href = props.backUrl;
}
</script>

<template>
    <SphSpinner
        v-if="loading"
        variant="centered"
    />
    <div v-else>
        <SphButton
            variant="ghost"
            :label="i18n.t('Zurück zur Übersicht')"
            @click="cancel"
            class="mb-4"
        />

        <div class="flex items-center justify-between">
            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ isEditMode ? i18n.t('Verein bearbeiten') : i18n.t('Verein anlegen') }}
            </h3>
        </div>

        <div class="kgr-inputGroup flex flex-col gap-4">
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('Name')"
                        required
                        :errors="errors.name"
                    >
                        <SphInput
                            v-model="club.name"
                            :invalid="!!errors.name"
                            maxlength="255"
                        />
                    </SphField>
                </div>
            </div>

            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('Rechtsform')"
                        :errors="errors.corporateType"
                    >
                        <SphSelect
                            v-model="club.corporateType"
                            :options="corporateTypeOptions"
                            :invalid="!!errors.corporateType"
                        />
                    </SphField>
                </div>
            </div>

            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('Straße')"
                        :errors="errors.street"
                        required
                    >
                        <SphInput
                            v-model="club.street"
                            :invalid="!!errors.street"
                            maxlength="255"
                        />
                    </SphField>
                </div>
            </div>

            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('PLZ')"
                        :errors="errors.zipcode"
                        required
                    >
                        <SphInput
                            v-model="club.zipcode"
                            :invalid="!!errors.zipcode"
                            maxlength="20"
                        />
                    </SphField>
                </div>
            </div>

            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('Ort')"
                        :errors="errors.city"
                        required
                    >
                        <SphInput
                            v-model="club.city"
                            :invalid="!!errors.city"
                            maxlength="255"
                        />
                    </SphField>
                </div>
            </div>

            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                <div class="kgr-grid__cell">
                    <SphField
                        :label="i18n.t('Bundesland')"
                        :errors="errors.province"
                    >
                        <SphSelect
                            v-model="club.province"
                            :options="provinceOptions"
                            :invalid="!!errors.province"
                        />
                    </SphField>
                </div>
            </div>
        </div>

        <div class="kgr-text kgr-text--hint kgr-text--rightAligned kgr-color--gray mt-4">
            * {{ i18n.t('Pflichtfelder') }}
        </div>
        <SphSeparator class="mb-4" />

        <div class="flex justify-between">
            <SphButton
                :label="i18n.t('Abbrechen')"
                @click="cancel"
            />
            <SphButton
                variant="primary"
                :label="i18n.t('Speichern')"
                :disabled="!isValid"
                :loading="pending"
                @click="save()"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.sph-select {
    width: 100%;
    :deep(> div) {
        width: 100%;
    }
}
</style>
